/**
 * Extracts a user-facing error message from an unknown caught value.
 *
 * Priority:
 * 1. `code` present → try `t('invoice_reports.error.<code>')`.
 *    If the key is found (translated value differs from the key), use it.
 * 2. `messages` array present → join with newline.
 * 3. Otherwise → undefined (caller uses its own fallback key).
 *
 * Adding support for a new error code requires only a new i18n entry:
 *   `invoice_reports.error.<CODE>: "message"`
 */
export function getStatusCodeFromError(error: unknown): number | undefined {
  return error !== null &&
    typeof error === 'object' &&
    'statusCode' in error &&
    typeof error.statusCode === 'number'
    ? error.statusCode
    : undefined;
}

export function extractInvoiceReportErrorMessage(
  error: unknown,
  t: (key: string) => string
): string | undefined {
  if (error === null || typeof error !== 'object') return undefined;

  if ('code' in error && typeof error.code === 'string') {
    const key = `invoice_reports.error.${error.code.toLowerCase()}`;
    const translated = t(key);
    if (translated !== key) {
      return translated;
    }
  }

  if (
    'messages' in error &&
    Array.isArray(error.messages) &&
    error.messages.length > 0
  ) {
    return error.messages.join('\n');
  }

  return undefined;
}
